import {Inject, Pipe, PipeTransform} from '@angular/core';
import {NiceValuePipe} from '../shared.pipe';
import {I18NDatePipe, I18NTimestampPipe} from '../../i18n/i18n.pipe';
import {I18NService} from '../../i18n/i18n.service';
import {AuthService} from 'auth-commons';
import {isDefined} from '../../commons/commons';
import {SharedEnergyData, TextNode} from './shared-energy-chart.component';
import {APP_CONFIG_TOKEN, AppConfig} from '../../app.config';
import {TIME_DELTA_DAY} from '../line-chart/line-chart.pipe';

interface TooltipData {
    text: string;
    color?: string;
    italic?: boolean;
    bold?: boolean;
}

@Pipe({
    name: 'sharedEnergyTooltipFormatter',
    pure: true
})
export class SharedEnergyTooltipFormatterPipe implements PipeTransform {

    private readonly nvp: NiceValuePipe;
    private readonly tsp: I18NTimestampPipe;
    private readonly dp: I18NDatePipe;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly i18n: I18NService,
                private readonly auth: AuthService) {
        this.nvp = new NiceValuePipe(i18n);
        this.tsp = new I18NTimestampPipe(i18n, auth);
        this.dp = new I18NDatePipe(i18n, auth);
    }

    transform(uom: string, effectiveEnergy?: boolean, timeDelta?: number, showEstimateTooltip?: boolean): (data: SharedEnergyData) => TextNode[][] {
        return (data: SharedEnergyData) => {
            const tooltipData: TooltipData[][] = [
                [{text: this.getDateForTooltip(data, timeDelta), italic: true}]
            ];
            if (isDefined(data.injectedEnergy)) {
                const injected = [
                    {text: '\u2022   ', color: this.cfg.shared.colors.effectiveInjectedEnergy, bold: true},
                    {
                        text: showEstimateTooltip ? this.i18n.translate(effectiveEnergy ? 'Effective injected energy (estimate)' : 'Injected energy (estimate)') + ': ' :
                            this.i18n.translate(effectiveEnergy ? 'Effective injected energy' : 'Injected energy') + ': ',
                        bold: true
                    },
                    {
                        text: isDefined(data.injectedEnergy) ? this.nvp.transform(data.injectedEnergy, 'kWh') : this.i18n.translate('No data'),
                        bold: true
                    }
                ];
                tooltipData.push(injected);
            }
            if (isDefined(data.accountedInjectedEnergy)) {
                const accountedInjected = [
                    {text: '\u2022   ', color: this.cfg.shared.colors.effectiveInjectedEnergy, bold: true},
                    {
                        text: this.i18n.translate(effectiveEnergy ? 'Effective injected energy (billed)' : 'Injected energy (billed)') + ': ',
                        bold: true
                    },
                    {
                        text: this.nvp.transform(data.accountedInjectedEnergy, 'kWh'),
                        bold: true
                    }
                ];
                tooltipData.push(accountedInjected);
            }
            if (isDefined(data.absorbedEnergy)) {
                const absorbed = [
                    {text: '\u2022   ', color: this.cfg.shared.colors.absorbedPower, bold: true},
                    {
                        text: showEstimateTooltip ? this.i18n.translate('Absorbed energy (estimate)') + ': ' : this.i18n.translate('Absorbed energy') + ': ',
                        bold: true
                    },
                    {
                        text: isDefined(data.absorbedEnergy) ? this.nvp.transform(data.absorbedEnergy, 'kWh') : this.i18n.translate('No data'),
                        bold: true
                    }
                ];
                tooltipData.push(absorbed);
            }
            if (isDefined(data.accountedAbsorbedEnergy)) {
                const accountedAbsorbed = [
                    {text: '\u2022   ', color: this.cfg.shared.colors.absorbedPower, bold: true},
                    {
                        text: this.i18n.translate('Absorbed energy (billed)') + ': ',
                        bold: true
                    },
                    {
                        text: this.nvp.transform(data.accountedAbsorbedEnergy, 'kWh'),
                        bold: true
                    }
                ];
                tooltipData.push(accountedAbsorbed);
            }
            if (isDefined(data.sharedEnergy)) {
                const shared = [
                    {text: '\u2022   ', color: this.cfg.shared.colors.sharedPower, bold: true},
                    {
                        text: showEstimateTooltip ? this.i18n.translate('Shared energy (estimate)') + ': ' : this.i18n.translate('Shared energy') + ': ',
                        bold: true
                    },
                    {
                        text: isDefined(data.sharedEnergy) ? this.nvp.transform(data.sharedEnergy, 'kWh') : this.i18n.translate('No data'),
                        bold: true
                    }
                ];
                tooltipData.push(shared);
            }
            if (isDefined(data.accountedSharedEnergy)) {
                const accountedShared = [
                    {text: '\u2022   ', color: this.cfg.shared.colors.sharedPower, bold: true},
                    {
                        text: this.i18n.translate('Shared energy (billed)') + ': ',
                        bold: true
                    },
                    {
                        text: this.nvp.transform(data.accountedSharedEnergy, 'kWh'),
                        bold: true
                    }
                ];
                tooltipData.push(accountedShared);
            }
            return tooltipData;
        };
    }

    private getDateForTooltip(data: SharedEnergyData, timeDelta?: number) {
        if (isDefined(timeDelta)) {
            return timeDelta === TIME_DELTA_DAY ?
                this.dp.transform(data?.time) :
                this.tsp.transform(data?.time) + ' - ' + this.tsp.transform(data?.time + timeDelta);
        }
        return this.tsp.transform(data?.time);
    }
}
